import { isNil } from 'lodash';

import { getConditionalObject } from 'utils/data';
import { getCompanyId } from 'utils/domain';

import { SKIP_FORBIDDEN } from './httpConfig';

// Per-section ApplyForm GET/PUT path segments. DEV-5639 added the six scalar
// company-profile sections + dbas; loan-production / questionnaire-answers stay
// read-only (their PUT bodies are complex composites, deferred).
const PROFILE_SECTION_PATHS = {
  owners: 'owners',
  affiliates: 'affiliates',
  investors: 'investors',
  warehouseLines: 'warehouse-lines',
  deliveryChannels: 'delivery-channels',
  companyDetails: 'company-details',
  licensedOriginator: 'licensed-originator',
  administrator: 'administrator',
  agencyApprovals: 'agency-approvals',
  compensationDelivery: 'compensation-delivery',
  contractServices: 'contract-services',
  dbas: 'dbas',
};

// The cert-scoped form-bundle routes (TpoFormBundleController) are decorated with
// RequireCompanyIdHeader; the auth-token interceptor only injects Authorization, so
// each call must carry the CompanyId header explicitly.
const getCompanyIdHeaders = () => ({ CompanyId: getCompanyId() });

const getCertificationFormBundlePath = certificationId =>
  `/api/tpo-signup/certifications/${certificationId}/form-bundle`;

/**
 * Direct ApplyForm client for the native TPO Certification tab (DEV-3026).
 * Talks to the ApplyForm backend through httpApplyApi (NOT the skynet gateway).
 */
export class TpoCertificationApi {
  #http;

  constructor(http) {
    this.#http = http;
  }

  // DEV-3100: mint a single-use handoff token so a lender admin can launch the
  // TPO certification wizard on behalf of a SubCompany. The handoff-mint and
  // iframe-base-url endpoints both live on ApplyForm — this client rides
  // httpApplyApi, NOT the skynet gateway, so the routes resolve correctly.
  async mintIframeHandoff({ subCompanyId }) {
    return this.#http.post('/api/admin-panel/tpo-iframe-handoff', { subCompanyId });
  }

  async getIframeBaseUrl({ subCompanyId }) {
    return this.#http.get('/api/admin-panel/tpo-certifications/iframe-base-url', {
      params: { subCompanyId },
    });
  }

  // Flat TpoCertificationDto for the SubCompany (header binds directly to this).
  async getActive({ subCompanyId }) {
    return this.#http.get(
      `/api/admin-panel/sub-companies/${subCompanyId}/tpo-certifications/active`,
    );
  }

  // Returns TpoSubCompanySignupConfigDto, or 204 when the SubCompany has no signup application.
  // The full configuration is deliberately NOT read here: its `settings` blob carries the lender's
  // decisioning rules, AI wiring and step expressions, which a TPO user reading their own company
  // surface must never receive. skipResponseInterceptor.forbiddenError keeps a cross-company 403
  // from triggering the global page reload — provenance is enhancement-only and the caller
  // swallows any failure to null.
  async getSignupConfig({ subCompanyId }) {
    return this.#http.get(`/api/admin-panel/sub-companies/${subCompanyId}/signup-config`, {
      skipResponseInterceptor: { forbiddenError: true },
    });
  }

  // TpoStepEvaluationResponseDto: { signupSteps, certificationSteps, recertificationSteps,
  // isRecertification }. Each step is the SERVER-evaluated TpoStepFlowDto — already filtered by
  // `isEnabled` and by its `conditionExpression`, with per-item questionnaire conditions applied.
  // The browser cannot run the expression engine, so this is the only faithful source for "which
  // steps did this broker actually see" (DEV-5251).
  // `skipForbidden` is for enhancement-only reads from the broker's own Company Manager surface:
  // a 403 there must degrade to "no summary" instead of tripping the global forbidden page-reload
  // (which would re-mount the reader and loop). The LOS review surface keeps the default.
  async getEvaluatedSteps({ subCompanyId, certificationId, skipForbidden = false }) {
    return this.#http.get(
      `/api/admin-panel/sub-companies/${subCompanyId}/tpo-certifications/${certificationId}/steps`,
      skipForbidden ? SKIP_FORBIDDEN : {},
    );
  }

  // Response: GetActiveTpoCertificationResponse
  // { activeCertification: { tpoCertificationId, pageStatus, certifiedUntil, cycleType } | null }.
  async getActivePage({ subCompanyId }) {
    return this.#http.get('/api/admin-panel/tpo-certifications/active', {
      params: { subCompanyId },
    });
  }

  async getHistory({ subCompanyId }) {
    return this.#http.get(
      `/api/admin-panel/sub-companies/${subCompanyId}/tpo-certifications/history`,
    );
  }

  // can-start-new-cycle lives on TpoCertificationAdminController as a query-based
  // route (not under sub-companies/{id}), unlike active/history/new-cycle.
  async getCanStartNewCycle({ subCompanyId }) {
    return this.#http.get('/api/admin-panel/tpo-certifications/can-start-new-cycle', {
      params: { subCompanyId },
    });
  }

  async startNewCycle({ subCompanyId }) {
    return this.#http.post(
      `/api/admin-panel/sub-companies/${subCompanyId}/tpo-certifications/new-cycle`,
    );
  }

  async getProfile({ subCompanyId, certificationId, skipForbidden = false }) {
    return this.#http.get(
      `/api/admin-panel/sub-companies/${subCompanyId}/tpo-certifications/${certificationId}/profile`,
      skipForbidden ? SKIP_FORBIDDEN : {},
    );
  }

  // TpoDocumentDto[] for everything uploaded against the certification, keyed to its document
  // slot. Lives on the TPO-signup surface (TpoDocumentController), not the admin-panel one, so it
  // carries the CompanyId header the same way the cert-scoped form-bundle routes do. The read is
  // enhancement-only on the broker's own Company Manager surface: a 403 must degrade to "no
  // documents to show" instead of tripping the global forbidden page-reload.
  async getCertificationDocuments({ certificationId, skipForbidden = false }) {
    return this.#http.get(`/api/tpo-signup/certifications/${certificationId}/documents`, {
      headers: getCompanyIdHeaders(),
      ...(skipForbidden ? SKIP_FORBIDDEN : {}),
    });
  }

  async getProfileSection({ subCompanyId, certificationId, section }) {
    return this.#http.get(
      `/api/admin-panel/sub-companies/${subCompanyId}/tpo-certifications/${certificationId}/profile/${PROFILE_SECTION_PATHS[section]}`,
    );
  }

  async updateProfileSection({ subCompanyId, certificationId, section, body }) {
    return this.#http.put(
      `/api/admin-panel/sub-companies/${subCompanyId}/tpo-certifications/${certificationId}/profile/${PROFILE_SECTION_PATHS[section]}`,
      body,
    );
  }

  async approve({ id }) {
    return this.#http.post(`/api/admin-panel/tpo-certifications/${id}/approve`);
  }

  // Body is { denial } once the company has denial reasons for the action, else the legacy { reason }.
  async reject({ id, ...body }) {
    return this.#http.post(`/api/admin-panel/tpo-certifications/${id}/reject`, body);
  }

  async revoke({ id, ...body }) {
    return this.#http.post(`/api/admin-panel/tpo-certifications/${id}/revoke`, body);
  }

  // TpoDenialCategoryDto[] holding only the enabled reasons available for `action` (Reject | Revoke).
  async getDenialCategories({ action }) {
    return this.#http.get('/api/admin-panel/tpo-denial-categories', {
      params: { action },
      ...SKIP_FORBIDDEN,
    });
  }

  // TpoCertificationDenialDto[]: internal denial history, refused for a broker token.
  async getCertificationDenials({ certificationId }) {
    return this.#http.get(
      `/api/admin-panel/tpo-certifications/${certificationId}/denials`,
      SKIP_FORBIDDEN,
    );
  }

  // --- Cert-scoped form-bundle signing (DEV-3863) -------------------------------------------------
  // The deferred "Authorized Signer Participation" task reuses DEV-3551's cert-scoped signing
  // endpoints: the BE resolves application / sub-company / signer / eligibility entirely from the
  // {certificationId} + the logged-in user, so a signer other than the in-session form-filler can
  // sign. ITpoFormBundleService.InitiateSigningSessionAsync is the sole writer of SigningRequestId.

  // TpoFormBundleDto: { formBundleId, name, signingUrl, status, signingRequestId }.
  async getFormBundleForCertification({ certificationId }) {
    return this.#http.get(getCertificationFormBundlePath(certificationId), {
      headers: getCompanyIdHeaders(),
    });
  }

  // TpoSignerEligibilityDto: { isEligible, matchedSignerName, matchedSignerEmail, authorizedSigners }.
  // 403 when the logged-in user is not an authorized signer for the cert's sub-company.
  async getSignerEligibilityForCertification({ certificationId }) {
    return this.#http.get(`${getCertificationFormBundlePath(certificationId)}/signer-eligibility`, {
      headers: getCompanyIdHeaders(),
    });
  }

  // Begins the signing session (writes SigningRequestId). Empty body; formBundleId rides the query.
  async beginSigningForCertification({ certificationId, formBundleId }) {
    return this.#http.post(`${getCertificationFormBundlePath(certificationId)}/sign`, null, {
      headers: getCompanyIdHeaders(),
      params: { formBundleId },
    });
  }

  // TpoSigningTokenDto: { accessToken, signerName, signingRequestId } — the signer-scoped Accounts
  // token the embedded signing surface authenticates to Skynet.Signing with.
  async getSigningTokenForCertification({ certificationId }) {
    return this.#http.post(
      `${getCertificationFormBundlePath(certificationId)}/signing-token`,
      null,
      {
        headers: getCompanyIdHeaders(),
      },
    );
  }

  // Live status read back from Skynet.Signing via SigningRequestId; 'Completed' is terminal.
  async getSigningStatusForCertification({ certificationId }) {
    return this.#http.get(`${getCertificationFormBundlePath(certificationId)}/status`, {
      headers: getCompanyIdHeaders(),
    });
  }

  // --- Signer-only step completion (DEV-3910) ----------------------------------------------------
  // The deferred Attestation / Compensation-Plans tasks (generic "Authorized Signer — Complete Step"
  // type) are completed by the authorized signer through a standalone surface in AdminPanel, not by
  // re-entering the cert wizard. These routes live on TpoCompanyProfileAdminController (the same base
  // getProfileSection / updateProfileSection ride); the signer is an AdminPanel user with a full
  // Accounts token, so certificationId travels as a query param and there is no iframe re-scope.

  // Returns the current attestation / comp-plans slice for the step (mirror of getProfileSection's
  // response shape) so the surface can pre-fill the signer's data-entry form. Both signer-step routes
  // 403 for a caller who is not the cert's authorized signer; the surface renders that inline, so the
  // global forbidden interceptor must not reload the page over it.
  // A multi-instance step (Questionnaire) also passes stepInstanceId to pick the instance; it is
  // absent for Attestation / CompensationPlans (single instance per certification).
  async getSignerStepState({ certificationId, stepKey, stepInstanceId = null }) {
    return this.#http.get(`/api/admin-panel/tpo-certifications/signer-step/${stepKey}`, {
      params: {
        certificationId,
        ...getConditionalObject(!isNil(stepInstanceId), () => ({ stepInstanceId })),
      },
      ...SKIP_FORBIDDEN,
    });
  }

  // Persists the signer's step data via WriteStepDataAsync, captures signer identity, and closes the
  // task. Body mirrors TpoCertificationStepWriteDto verbatim ({ diff, pathToEntity,
  // expectedModifiedDate }) — the field types must match the BE dictionary exactly (DEV-3329).
  async completeSignerStep({ certificationId, stepKey, body }) {
    return this.#http.post(
      `/api/admin-panel/tpo-certifications/signer-step/${stepKey}/complete`,
      body,
      { params: { certificationId }, ...SKIP_FORBIDDEN },
    );
  }
}
